/* eslint-disable no-magic-numbers */
import { NewReaction } from '@/types/models';
import { getReactionLines } from '@/components/FunctionalArea/Modal/EditReactionModal/utils/getReactionLines';

/** Axis-aligned bounds, in diagram coordinates. */
export type ReactionBoundingBox = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};

/**
 * Smallest box containing every segment of every line the reaction owns - its
 * own line plus the lines of each reactant, product, modifier and operator.
 * This is what the preview frames, so that editing any line shows the whole
 * reaction rather than just its central line.
 *
 * Returns undefined when the reaction has no segments at all, in which case
 * there is nothing to frame.
 */
export const getReactionBoundingBox = (reaction: NewReaction): ReactionBoundingBox | undefined => {
  const segments = getReactionLines(reaction).flatMap(({ line }) => line.segments);
  if (segments.length === 0) {
    return undefined;
  }

  return segments.reduce<ReactionBoundingBox>(
    (box, segment) => ({
      minX: Math.min(box.minX, segment.x1, segment.x2),
      minY: Math.min(box.minY, segment.y1, segment.y2),
      maxX: Math.max(box.maxX, segment.x1, segment.x2),
      maxY: Math.max(box.maxY, segment.y1, segment.y2),
    }),
    {
      minX: Number.POSITIVE_INFINITY,
      minY: Number.POSITIVE_INFINITY,
      maxX: Number.NEGATIVE_INFINITY,
      maxY: Number.NEGATIVE_INFINITY,
    },
  );
};
